import { computed, inject, Injector, Type } from '@angular/core';
import {
  signalStore,
  withComputed,
  withFeature,
  withProps,
  withState,
} from '@ngrx/signals';

import {
  EXTERNAL_TODO_TASK_STATUSES,
  TaskInstance,
  TaskListSignalState,
} from '@data-access/tasks/types';
import { TasksService } from '@data-access/tasks/services';
import { DEFAULT_TASK_LIST_STATE } from '@data-access/tasks/contants';
import { withTaskEntities } from './entities';
import { DOCUMENT_TASKS } from '@data-access/tasks/contants/task.constants';
import { Dispatcher, Events } from '@ngrx/signals/events';
import { withTaskEffects } from '@data-access/tasks/store/signal-store/effects';
import { withDevtools } from '@angular-architects/ngrx-toolkit';

/**
 * Returns only unresolved tasks with thread for collabhub
 * @private
 **/
function getUnresolvedCollabhubTasks(tasks: TaskInstance[]): TaskInstance[] {
  return tasks
    .filter((task) => task.thread && !task.thread?.is_resolved)
    .filter((task) => EXTERNAL_TODO_TASK_STATUSES.includes(task.status));
}

/*
 * @private
 * Helper function to get unresolved collabhub tasks count per section
 **/
function _getUnresolvedCollabhubTasksForSectionMap(
  tasks: TaskInstance[],
  sectionMap: Map<string, number>,
): Map<string, number> {
  tasks
    .filter((task) => task.thread && !task.thread?.is_resolved)
    .filter((task) => task.status !== 'ready_for_review')
    .forEach((task) => {
      const sectionId = task.task_entity!.section_id!;
      sectionMap.set(sectionId, (sectionMap.get(sectionId) || 0) + 1);
    });
  return sectionMap;
}

export const TASK_LIST_SIGNAL_STORE = signalStore(
  withDevtools('taskList'), // Added for Redux DevTools integration
  withState(() => inject(DEFAULT_TASK_LIST_STATE)),
  withProps((store, _taskListService = inject(TasksService)) => ({
    _dispatcher: inject(Dispatcher),
    _events: inject(Events),
    _injector: inject(Injector),
    _taskListService,
    store,
  })),
  withFeature(({ _injector }) => withTaskEntities(_injector)),
  withFeature(({ _events, _taskListService, _dispatcher }) =>
    withTaskEffects(_events, _taskListService, _dispatcher),
  ),
  withComputed(
    ({
      _questionsEntities,
      _documentsEntities,
      _sectionsEntities,
      _taskEntities,
      isSMEQCLoading,
      tasksAlreadyLoaded,
      ...store
    }) => ({
      /** Computes any tasks with the type matching a known DOCUMENT_TASK type
       * @returns An array of document-related TaskInstance objects sorted by updated_dt descending
       * */
      $documentTasks: computed(() => {
        const taskEntities = _taskEntities();
        if (!taskEntities) return [];
        return taskEntities
          .filter((task) => DOCUMENT_TASKS.has(task.task_type))
          .sort(
            (a, b) =>
              new Date(b.updated_dt ?? 0).getTime() -
              new Date(a.updated_dt ?? 0).getTime(),
          );
      }),

      $error: computed(() => store.error()),

      $filtered: computed(() => store.filtered()),

      $isLoading: computed(() => store.loading()),

      $isSMEQCLoading: computed(() => isSMEQCLoading()),

      /** Computes any tasks with the 'question' task entity type */
      $questionTasks: computed(() => {
        const _qTasks = _questionsEntities();
        if (!_qTasks) {
          return [];
        }
        return _qTasks;
      }),

      /** Computes any remaining 'document' SME QC tasks (internal, not completed) */
      $remainingDocumentSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.document;
      }),

      /** Computes remaining document tasks (type 'document') as a Map */
      $remainingDocumentToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _documentTasks = _remainingToDoTasks?.document;
        if (!_documentTasks?.length) {
          return new Map<string, TaskInstance>();
        }
        return new Map<string, TaskInstance>(
          _documentTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                task.task_type === 'document',
            )
            .map((task) => [task.id, task]),
        );
      }),

      /** Computes remaining question tasks (type 'question') as a Map */
      $remainingQuestionToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _questionTasks = _remainingToDoTasks?.question;
        if (!_questionTasks?.length) {
          return new Map<string, TaskInstance>();
        }

        return new Map<string, TaskInstance>(
          _questionTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                EXTERNAL_TODO_TASK_STATUSES.includes(task.status) &&
                !!task.task_entity?.question_id,
            )
            .map((task) => [task.task_entity!.question_id!, task]),
        );
      }),

      /** Computes any remaining 'other' SME QCtasks (internal, not completed) */
      $remainingOtherSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.other;
      }),

      /** Computes any remaining 'question' SME QC tasks (internal, not completed) */
      $remainingQuestionSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.question;
      }),

      /** Computes remaining requested document tasks as a Map */
      $remainingRequestedDocumentToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _documentTasks = _remainingToDoTasks?.document;
        if (!_documentTasks?.length) {
          return new Map<string, TaskInstance>();
        }
        return new Map<string, TaskInstance>(
          _documentTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                task.task_type === 'document_type_requested',
            )
            .map((task) => [task.id, task]),
        );
      }),

      /** Computes remaining section tasks (type 'section') as a Map */
      $remainingSectionToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _sectionTasks = _remainingToDoTasks?.section;
        if (!_sectionTasks?.length) {
          return new Map<string, TaskInstance>();
        }

        return new Map<string, TaskInstance>(
          _sectionTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                EXTERNAL_TODO_TASK_STATUSES.includes(task.status) &&
                !!task.task_entity?.section_id,
            )
            .map((task) => [task.task_entity!.section_id!, task]),
        );
      }),

      /** Computes any remaining SME QC tasks (internal, not completed) */
      $remainingSMEQCTasks: computed(() => {
        return store.remainingSMEQCTasks();
      }),

      /** Computes any remaining 'section' tasks (internal, not completed) */
      $remainingSectionSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.section;
      }),

      /**Computes remaining 'section' and 'question' task todo count under each section */
      $remainingSectionTaskTodoCount: computed(() => {
        const _remainingTodoTasks = store.remainingToDoTasks();
        if (!_remainingTodoTasks) {
          return new Map<string, number>([]);
        }
        const sectionTaskCountMap = new Map<string, number>();
        _getUnresolvedCollabhubTasksForSectionMap(
          _remainingTodoTasks.section,
          sectionTaskCountMap,
        );
        _getUnresolvedCollabhubTasksForSectionMap(
          _remainingTodoTasks.question,
          sectionTaskCountMap,
        );
        return sectionTaskCountMap;
      }),

      /** Computes any tasks that are marked as to-do (not completed, not internal) */
      $remainingToDoCollabHubTasks: computed(() => {
        return store.remainingToDoTasks();
      }),

      /** Computes count of remaining to-do detail tasks */
      // MisLeading name
      $remainingToDoDetailTaskCount: computed(() => {
        const remainingToDoTasks = store.remainingToDoTasks();
        if (!remainingToDoTasks) {
          return 0;
        }
        return (
          getUnresolvedCollabhubTasks(remainingToDoTasks.question).length +
          getUnresolvedCollabhubTasks(remainingToDoTasks.section).length
        );
      }),

      // UI state
      /** Computes count of remaining to-do 'document' tasks */
      $remainingToDoDocumentTaskCount: computed(() => {
        const remainingToDoTasks = store.remainingToDoTasks();
        if (!remainingToDoTasks) {
          return 0;
        }
        return getUnresolvedCollabhubTasks(remainingToDoTasks.document).length;
      }),
      /** Computes any tasks with the 'section' task entity type */
      $sectionTasks: computed(() => {
        const _sectionTaskEntities = _sectionsEntities();
        if (!_sectionTaskEntities) {
          return [];
        }
        return _sectionTaskEntities;
      }),
      /** Computes all fetched tasks */
      $tasks: computed(() => {
        const taskEntities = _taskEntities();
        if (!taskEntities) {
          return [];
        }
        return taskEntities;
      }),
      /** Computes all tasks that are associated with a collabhub thread. Threads are ordered as unresolved first **/
      $threads: computed(() => {
        const taskEntities = _taskEntities();
        if (!taskEntities) {
          return [];
        }
        // Sort threads: unresolved first, then resolved
        return taskEntities
          .filter((task) => task.thread)
          .sort(
            (a, b) =>
              Number(a.thread?.is_resolved) - Number(b.thread?.is_resolved),
          );
      }),
      $tasksAlreadyLoaded: computed(() => tasksAlreadyLoaded())
    }),
  ),
) satisfies Type<TaskListSignalState>;
